import { useCallback, useState } from "react";
import { useToast } from "@/components/shared/Toast";
import { getRuntimeConfig } from "@/config/runtime";
import { ALLOWED_IMAGE_TYPES } from "@/constants/files";
import { useFileDropzone } from "@/hooks/useFileDropzone";
import { partitionUploadFiles } from "@/lib/files";
import { googleDriveService, sharepointService } from "@/lib/services";
import type { SharePointDocumentMeta, SharePointItem } from "@/types";

/** Regex that matches SharePoint / OneDrive document URLs. */
const SHAREPOINT_URL_RE = /https?:\/\/[a-zA-Z0-9-]+(?:-my)?\.sharepoint\.com\/[^\s)>"]+/g;

/**
 * Result of downloading SharePoint documents — returned by
 * `downloadSharePointDocuments` so the caller can embed them
 * into the AI request.
 */
export interface SharePointDownloadResult {
  metas?: SharePointDocumentMeta[];
  contentText?: string;
  slideImages?: string[];
}

/**
 * Encapsulates all attachment-related state & logic extracted from Chat.tsx.
 *
 * Manages: file uploads (images + documents), SharePoint file chips,
 * SP URL detection on paste, drag-and-drop, and SP document downloading.
 */
export function useChatAttachments(opts: {
  modelSupportsVision: boolean;
  inputValue: string;
  setInputValue: (v: string | ((prev: string) => string)) => void;
  allowedExtensions?: ReadonlySet<string>;
}) {
  const { modelSupportsVision, inputValue, setInputValue, allowedExtensions } = opts;
  const { showToast } = useToast();

  const keepSupportedFiles = useCallback(
    (files: File[]) => {
      if (!allowedExtensions) return files;
      const { accepted, rejected } = partitionUploadFiles(files, allowedExtensions);
      if (rejected.length > 0) {
        showToast(`Unsupported file type skipped: ${rejected.join(", ")}`, "warning");
      }
      return accepted;
    },
    [allowedExtensions, showToast],
  );

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [sharepointFiles, setSharepointFiles] = useState<SharePointItem[]>([]);
  const [isSharePointBrowserOpen, setIsSharePointBrowserOpen] = useState(false);
  const [isDownloadingFiles, setIsDownloadingFiles] = useState(false);
  const [isResolvingUrl, setIsResolvingUrl] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);

  // ---------------------------------------------------------------------------
  // SharePoint URL detection & auto-resolve on paste
  // ---------------------------------------------------------------------------

  /**
   * Extract all SharePoint URLs from a string and return them together with
   * the remaining text (URLs stripped out).
   */
  const extractSharePointUrls = useCallback((text: string) => {
    const urls = text.match(SHAREPOINT_URL_RE) || [];
    if (urls.length === 0) return { urls: [], remainingText: text };

    let remaining = text;
    for (const url of urls) {
      remaining = remaining.replace(url, "");
    }
    // Clean up leftover whitespace
    remaining = remaining.replace(/\n{3,}/g, "\n\n").trim();
    return { urls, remainingText: remaining };
  }, []);

  /**
   * Resolve a SharePoint URL to a SharePointItem and add it to the
   * attached files list. Returns true on success so the caller can
   * decide whether to restore the URL to the input on failure.
   */
  const resolveAndAttachUrl = useCallback(async (url: string): Promise<boolean> => {
    try {
      const item = await sharepointService.resolveUrl(url);
      // Only attach files, not folders
      if (item.is_folder) return false;
      setSharepointFiles((prev) => {
        if (prev.some((f) => f.id === item.id)) return prev; // dedupe
        return [...prev, item];
      });
      return true;
    } catch (err) {
      console.warn("Could not resolve SharePoint URL:", url, err);
      return false;
    }
  }, []);

  /**
   * Handle paste events on the chat input. If the pasted content contains
   * images (from clipboard), add them as uploaded files. If the pasted text
   * contains SharePoint URLs, resolve them into file chips.
   */
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      // Check for pasted image files (e.g. screenshot from clipboard)
      if (modelSupportsVision && e.clipboardData.files.length > 0) {
        const imageFiles = Array.from(e.clipboardData.files).filter((f) => ALLOWED_IMAGE_TYPES.has(f.type));
        if (imageFiles.length > 0) {
          e.preventDefault();
          setUploadedFiles((prev) => [...prev, ...imageFiles]);
          return;
        }
      }

      const pasted = e.clipboardData.getData("text/plain");
      if (!pasted) return;

      const { urls, remainingText } = extractSharePointUrls(pasted);
      if (urls.length === 0) return; // nothing to intercept

      // Prevent the default paste so we can modify the value
      e.preventDefault();

      // Insert the non-URL remainder into the input at the cursor position
      const input = e.currentTarget as HTMLInputElement | HTMLTextAreaElement;
      const start = input.selectionStart ?? inputValue.length;
      const end = input.selectionEnd ?? inputValue.length;
      const newValue = inputValue.slice(0, start) + remainingText + inputValue.slice(end);
      setInputValue(newValue);

      // Resolve each URL in the background; put back any that fail
      setIsResolvingUrl(true);
      Promise.all(
        urls.map(async (url) => {
          const ok = await resolveAndAttachUrl(url);
          if (!ok) {
            // Couldn't resolve — restore the URL to the input
            setInputValue((prev: string) => (prev ? `${prev} ${url}` : url));
          }
        }),
      ).finally(() => setIsResolvingUrl(false));
    },
    [inputValue, extractSharePointUrls, resolveAndAttachUrl, modelSupportsVision, setInputValue],
  );

  // ---------------------------------------------------------------------------
  // File upload handlers
  // ---------------------------------------------------------------------------

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = keepSupportedFiles(Array.from(e.target.files || []));
    setUploadedFiles((prev) => [...prev, ...files]);
    // Reset the input so the same file(s) can be re-selected
    e.target.value = "";
  };

  /** Accept only images for the vision/image upload button */
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter((f) => ["image/jpeg", "image/png", "image/gif", "image/webp"].includes(f.type));
    if (imageFiles.length < files.length) {
      showToast("Some files were skipped (only JPEG, PNG, GIF, WebP allowed)", "warning");
    }
    setUploadedFiles((prev) => [...prev, ...imageFiles]);
    e.target.value = "";
  };

  // ---------------------------------------------------------------------------
  // Drag-and-drop support
  // ---------------------------------------------------------------------------

  const handleDroppedFiles = useCallback(
    (droppedFiles: File[]) => {
      // Accept images only if model supports vision; always accept documents
      const acceptedFiles = keepSupportedFiles(
        droppedFiles.filter(
          (f) => (ALLOWED_IMAGE_TYPES.has(f.type) && modelSupportsVision) || !ALLOWED_IMAGE_TYPES.has(f.type),
        ),
      );
      if (acceptedFiles.length > 0) {
        setUploadedFiles((prev) => [...prev, ...acceptedFiles]);
      }
    },
    [modelSupportsVision, keepSupportedFiles],
  );

  const {
    isDragging: isDraggingOver,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useFileDropzone(handleDroppedFiles);

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // ---------------------------------------------------------------------------
  // Cloud document download (SharePoint or Google Drive)
  // ---------------------------------------------------------------------------

  /**
   * Download content from all pending cloud files (SharePoint or Google Drive)
   * and return the metadata + text content for embedding in the AI request.
   *
   * Dispatches to the correct service based on the configured auth provider:
   * - Microsoft: downloads via SharePoint endpoint using `web_url`
   * - Google: downloads via Google Drive endpoint using file `id`
   *
   * Also calls `updateMessageMeta` to refresh the temp user message
   * with final resolved metadata.
   */
  const downloadSharePointDocuments = async (
    pendingFiles: SharePointItem[],
    updateMessageMeta: (metas: SharePointDocumentMeta[]) => void,
  ): Promise<SharePointDownloadResult> => {
    if (pendingFiles.length === 0) return {};

    const isGoogle = getRuntimeConfig().authProvider === "google";

    setIsDownloadingFiles(true);
    try {
      const metas: SharePointDocumentMeta[] = [];
      const documentSections: string[] = [];
      const allSlideImages: string[] = [];
      for (const file of pendingFiles) {
        try {
          const doc = isGoogle
            ? await googleDriveService.downloadDocument(file.id)
            : await sharepointService.downloadDocument(file.web_url);
          metas.push({
            filename: doc.filename,
            url: doc.source_url,
            size: doc.size,
            mimeType: doc.mime_type,
          });

          // Presentations return slide images instead of text content
          if (doc.slide_images && doc.slide_images.length > 0) {
            allSlideImages.push(...doc.slide_images);
            documentSections.push(
              `### ${doc.filename}\n*(Presentation with ${doc.slide_images.length} slides — images attached)*`,
            );
          } else {
            let docSection = `### ${doc.filename}\n`;
            if (doc.truncated) {
              docSection += `*(Document truncated — original size: ${doc.size} bytes)*\n`;
            }
            docSection += `\n${doc.content}`;
            documentSections.push(docSection);
          }
        } catch (err) {
          console.error(`Failed to download file ${file.name}:`, err);
          metas.push({
            filename: file.name,
            url: file.web_url,
            size: file.size,
            mimeType: file.mime_type,
          });
          documentSections.push(
            `### ${file.name}\n*Failed to download file content. Reference: [${file.name}](${file.web_url})*`,
          );
        }
      }

      if (metas.length > 0) {
        updateMessageMeta(metas);
      }

      return {
        metas: metas.length > 0 ? metas : undefined,
        contentText:
          documentSections.length > 0 ? `## Attached Documents\n\n${documentSections.join("\n\n---\n\n")}` : undefined,
        slideImages: allSlideImages.length > 0 ? allSlideImages : undefined,
      };
    } finally {
      setIsDownloadingFiles(false);
    }
  };

  /**
   * Snapshot and clear the current pending files (both uploaded + sharepoint).
   * Returns the pending files for use in handleSend.
   */
  const consumePendingFiles = () => {
    const pending = {
      imageFiles: uploadedFiles.filter((f) => ALLOWED_IMAGE_TYPES.has(f.type)),
      documentFiles: uploadedFiles.filter((f) => !ALLOWED_IMAGE_TYPES.has(f.type)),
      sharepointFiles: [...sharepointFiles],
    };
    setUploadedFiles([]);
    if (sharepointFiles.length > 0) {
      setSharepointFiles([]);
    }
    return pending;
  };

  /**
   * Reset all attachment state (used when starting a new chat).
   */
  const reset = () => {
    setUploadedFiles([]);
    setSharepointFiles([]);
  };

  return {
    // State
    uploadedFiles,
    sharepointFiles,
    isSharePointBrowserOpen,
    isDownloadingFiles,
    isResolvingUrl,
    isUploadingImages,
    isDraggingOver,
    // Setters (for UI bindings)
    setSharepointFiles,
    setIsSharePointBrowserOpen,
    setIsUploadingImages,
    // Event handlers
    handleFileUpload,
    handleImageUpload,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handlePaste,
    removeFile,
    // Actions
    consumePendingFiles,
    downloadSharePointDocuments,
    reset,
  };
}
